import {
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Box,
  Menu,
  MenuItem,
  IconButton,
  Typography,
} from '@mui/material'
import Icon, { MenuOverflowIcon } from '@design-systems/react-icons'
import { Star } from '@mui/icons-material'
import { listItemSx, listItemIconBadgeSx } from '../styles/NotificationStyle'
import { formatCreatedAt, getTypeColor } from '../../../helpers/utils'
import { useState } from 'react'

export default function NotificationItem({ notification, onMarkRead }) {
  const { id, event_subject, created_time, read, event_type } = notification
  const [menuAnchor, setMenuAnchor] = useState(null)
  const isMenuOpen = Boolean(menuAnchor)

  const handleMenuClick = (event) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleMarkAsRead = () => {
    if (!read && onMarkRead) {
      onMarkRead(notification)
    }
    handleMenuClose()
  }

  return (
    <ListItem
      key={id}
      onClick={() => !read && onMarkRead && onMarkRead(notification)}
      sx={{
        ...listItemSx(read),
        py: 1.5,
        px: 2,
        borderBottom: '1px solid #f5f5f5',
        '&:last-child': {
          borderBottom: 'none',
        },
        cursor: read ? 'default' : 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(255, 235, 59, 0.15)',
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 'auto', mr: 2 }}>
        <Badge
          variant="dot"
          color="primary"
          overlap="circular"
          invisible={read}
          sx={{
            ...listItemIconBadgeSx,
            '& .MuiBadge-badge': {
              right: -3,
              top: 3,
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#1976d2',
            },
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: getTypeColor(event_type, read),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Star
              sx={{
                color: 'white',
                fontSize: '1.1rem',
              }}
            />
          </Box>
        </Badge>
      </ListItemIcon>

      <ListItemText
        primary={
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: read ? 400 : 600,
                color: read ? 'text.secondary' : 'text.primary',
                lineHeight: 1.4,
                mb: 0.5,
              }}
            >
              {event_subject}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
              }}
            >
              {formatCreatedAt(created_time)}
            </Typography>
          </Box>
        }
        sx={{
          margin: 0,
          '& .MuiListItemText-primary': {
            margin: 0,
          },
        }}
      />

      <IconButton
        size="small"
        sx={{
          opacity: 0.6,
          ml: 1,
          '&:hover': {
            opacity: 1,
          },
        }}
        onClick={handleMenuClick}
      >
        <Icon svg={MenuOverflowIcon} size="sm" />
      </IconButton>

      <Menu
        anchorEl={menuAnchor}
        open={isMenuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: 140,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            borderRadius: 1,
          },
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
      >
        {!read && (
          <MenuItem
            onClick={handleMarkAsRead}
            sx={{
              py: 1,
              px: 2,
            }}
          >
            <Typography variant="body2" sx={{ color: 'inherit' }}>
              Mark as read
            </Typography>
          </MenuItem>
        )}
        {read && (
          <MenuItem disabled sx={{ py: 1, px: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
              Already read
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </ListItem>
  )
}
